/**
 * Documentation publique du connecteur MCP d'exiPlan — `/aide-mcp` (URL contractuelle,
 * `https://www.exiplan.fr/aide-mcp`, déclarée par le serveur MCP et soumise au Claude Directory).
 *
 * Page PUBLIQUE : aucune donnée de compte rendue, aucune dépendance à la session. Les extraits viennent
 * de `lib/mcp-snippets.js` (seule source) ; l'URL du serveur est `VITE_MCP_URL` si définie au
 * build, sinon l'URL de production documentée dans `mcp/README.md`.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MCP_CLI_CLIENT_ID, MCP_URL } from '../lib/api.js';
import { buildClaudeCodeSnippet, buildVsCodeMcpJson } from '../lib/mcp-snippets.js';
import { Eyebrow, PageHeader } from '../components/common.jsx';
import { McpCell, SnippetBlock } from '../components/McpParts.jsx';
import { CELLS_PLANCHE, TD, TH } from '../ui/theme.js';

/** Repli quand `VITE_MCP_URL` n'est pas définie au build (URL de production du serveur MCP). */
export const MCP_URL_PAR_DEFAUT = 'https://mcp.exiplan.fr/mcp';

/** Les 12 outils (source : `mcp/README.md` § Outils). Les effets sont dans `aideMcp.tools.*`. */
export const OUTILS = [
  { name: 'exiplan_profil_moi', mode: 'lecture' },
  { name: 'exiplan_projets_lister', mode: 'lecture' },
  { name: 'exiplan_projet_lire', mode: 'lecture' },
  { name: 'exiplan_tableau_lire', mode: 'lecture' },
  { name: 'exiplan_colonnes_lister', mode: 'lecture' },
  { name: 'exiplan_indicateurs_lire', mode: 'lecture' },
  { name: 'exiplan_taches_lister', mode: 'lecture' },
  { name: 'exiplan_tache_lire', mode: 'lecture' },
  { name: 'exiplan_membres_lister', mode: 'lecture' },
  { name: 'exiplan_tache_creer', mode: 'ecriture' },
  { name: 'exiplan_tache_modifier', mode: 'ecriture' },
  { name: 'exiplan_tache_deplacer', mode: 'ecriture' },
];

const ETAPES = ['1', '2', '3', '4'];
const EXEMPLES = ['1', '2', '3', '4'];
const DEPANNAGE = ['401', '403', '429', '503'];
const SUPPORT = 'cedric@exidis.fr';

function Titre({ children }) {
  return <p className="text-sm font-semibold text-ink">{children}</p>;
}

function Liste({ items }) {
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-soft">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function AideMcpPage() {
  const { t } = useTranslation();

  const url = MCP_URL || MCP_URL_PAR_DEFAUT;
  const claudeCode = MCP_CLI_CLIENT_ID
    ? buildClaudeCodeSnippet({ url, clientId: MCP_CLI_CLIENT_ID })
    : null;

  return (
    <div>
      <PageHeader eyebrow={t('aideMcp.eyebrow')} title={t('aideMcp.title')} />
      <div className="mx-auto max-w-(--largeur-corps) p-4">
        <div className={`${CELLS_PLANCHE} grid-cols-1 md:grid-cols-6`}>
          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.whatTitle')}</Eyebrow>
            <p className="mt-2 max-w-prose text-sm text-ink-soft">{t('aideMcp.whatBody')}</p>
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.prereqTitle')}</Titre>
            <Liste items={[t('aideMcp.prereq1'), t('aideMcp.prereq2'), t('aideMcp.prereq3')]} />
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.rightsTitle')}</Titre>
            <p className="mt-2 text-sm text-ink-soft">{t('aideMcp.rightsBody')}</p>
            <Liste items={[t('aideMcp.rights1'), t('aideMcp.rights2'), t('aideMcp.rights3')]} />
          </McpCell>

          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.connectTitle')}</Eyebrow>
            <Titre>{t('aideMcp.claudeAiTitle')}</Titre>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink-soft">
              {ETAPES.map((step) => (
                <li key={step}>{t(`aideMcp.claudeAiStep${step}`)}</li>
              ))}
            </ol>
            <p className="mt-2 text-xs text-ink-faint">{t('aideMcp.claudeAiNote')}</p>
            <p className="mt-3 text-sm text-ink-soft">{t('aideMcp.claudeAiCustom')}</p>
            <SnippetBlock value={url} />
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.claudeCodeTitle')}</Titre>
            <p className="mt-2 text-xs text-ink-faint">{t('aideMcp.claudeCodeBody')}</p>
            {claudeCode ? (
              <SnippetBlock value={claudeCode} />
            ) : (
              <p className="mt-2 text-xs text-ink-faint">{t('aideMcp.clientIdOnRequest')}</p>
            )}
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.vscodeTitle')}</Titre>
            <p className="mt-2 text-xs text-ink-faint">{t('aideMcp.vscodeBody')}</p>
            <SnippetBlock value={buildVsCodeMcpJson({ url })} />
          </McpCell>

          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.toolsTitle')}</Eyebrow>
            <p className="mt-2 text-sm text-ink-soft">{t('aideMcp.toolsIntro')}</p>
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('aideMcp.toolsColTool')}</th>
                  <th className={TH}>{t('aideMcp.toolsColMode')}</th>
                  <th className={TH}>{t('aideMcp.toolsColEffect')}</th>
                </tr>
              </thead>
              <tbody>
                {OUTILS.map(({ name, mode }) => (
                  <tr key={name}>
                    <td className={TD}>
                      <code className="font-mono text-[11px] text-ink-soft">{name}</code>
                    </td>
                    <td className={TD}>{t(`aideMcp.mode.${mode}`)}</td>
                    <td className={TD}>{t(`aideMcp.tools.${name}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-ink-faint">{t('aideMcp.toolsNote')}</p>
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.outOfScopeTitle')}</Titre>
            <p className="mt-2 text-sm text-ink-soft">{t('aideMcp.outOfScopeBody')}</p>
            <Liste
              items={[
                t('aideMcp.outOfScope1'),
                t('aideMcp.outOfScope2'),
                t('aideMcp.outOfScope3'),
                t('aideMcp.outOfScope4'),
              ]}
            />
          </McpCell>

          <McpCell>
            <Titre>{t('aideMcp.dataTitle')}</Titre>
            <p className="mt-2 text-sm text-ink-soft">{t('aideMcp.dataBody')}</p>
            <Liste items={[t('aideMcp.data1'), t('aideMcp.data2'), t('aideMcp.data3')]} />
          </McpCell>

          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.examplesTitle')}</Eyebrow>
            <ul className="mt-2 space-y-2">
              {EXEMPLES.map((n) => (
                <li
                  key={n}
                  className="border-2 border-line bg-paper p-3 font-mono text-xs text-ink"
                >
                  {t(`aideMcp.example${n}`)}
                </li>
              ))}
            </ul>
          </McpCell>

          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.troubleshootingTitle')}</Eyebrow>
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('mcpHelp.troubleshootingSymptomHeader')}</th>
                  <th className={TH}>{t('mcpHelp.troubleshootingCauseHeader')}</th>
                  <th className={TH}>{t('mcpHelp.troubleshootingActionHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {DEPANNAGE.map((row) => (
                  <tr key={row}>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.symptom`)}</td>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.cause`)}</td>
                    <td className={TD}>{t(`mcpHelp.troubleshooting.${row}.action`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </McpCell>

          <McpCell span="md:col-span-6">
            <Eyebrow>{t('aideMcp.supportTitle')}</Eyebrow>
            <p className="mt-2 text-sm text-ink-soft">
              {t('aideMcp.supportPrefix')}{' '}
              <a href={`mailto:${SUPPORT}`} className="underline">
                {SUPPORT}
              </a>
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              <Link to="/confidentialite" className="underline">
                {t('aideMcp.privacyLink')}
              </Link>
            </p>
          </McpCell>
        </div>
      </div>
    </div>
  );
}
